import { isEqual, omit, debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { JsonFormsRendererRegistryEntry } from "@jsonforms/core";
import { materialCells } from "@jsonforms/material-renderers";
import { JsonForms } from "@jsonforms/react";
import { Layout } from "@jsonforms/core";

import { useDispatch, usePropertiesService, useSchema, useSelection, useUiSchema } from "../../core/context";
import { Actions } from "../../core/model";
import { EditorUISchemaElement } from "../../core/model/uischema";
import { tryFindByUUID } from "../../core/util/schemasUtil";
import { PropertySchemas } from "../propertiesService";
import { addSchemaOptionsProperty, createPropertyControl } from "../schemaDecorators";
import { CustomUISchemaElement } from "../../../../services/applications/types";

export interface PropertiesProps {
	propertyRenderers: JsonFormsRendererRegistryEntry[];
}

export const Properties: React.FC<PropertiesProps> = ({ propertyRenderers }) => {
	const [selection] = useSelection();
	const uiSchema = useUiSchema();
	const schema = useSchema();
	const dispatch = useDispatch();
	const [properties, setProperties] = useState<PropertySchemas>();

	const [localData, setLocalData] = useState<any>({});
	const propertiesService = usePropertiesService();

	const uiElement: EditorUISchemaElement | undefined = useMemo(
		() => (selection?.uuid ? tryFindByUUID(uiSchema, selection.uuid) : undefined),
		[selection?.uuid, uiSchema]
	);

	useEffect(() => {
		if (!uiElement) return;

		//@ts-ignore
		if (uiElement?.options?.addressOrder) {
			//@ts-ignore
			uiElement.addressOrder = uiElement?.options?.addressOrder;
			delete uiElement?.options?.addressOrder;
		}

		//@ts-ignore
		if (uiElement?.options?.elementLabelProp) {
			//@ts-ignore
			uiElement.elementLabelProp = uiElement?.options?.elementLabelProp;
			delete uiElement?.options?.elementLabelProp;
		}
	}, [uiElement, uiElement?.uuid]);

	const data: Partial<CustomUISchemaElement> = useMemo(() => {
		if (!uiElement) return {};
		let originalData = omit(uiElement, ["uuid", "parent", "elements", "linkedSchemaElement", "options.detail"]);

		if (!properties) return originalData;

		const optionDefaults: {
			[key: string]: any;
		} = {};

		//@ts-ignore
		if (
			properties?.schema?.properties?.options?.properties?.addable &&
			typeof originalData?.options?.addable === "undefined"
		) {
			optionDefaults.addable = true;
		}
		//@ts-ignore
		if (
			properties?.schema?.properties?.options?.properties?.removable &&
			typeof originalData?.options?.removable === "undefined"
		) {
			optionDefaults.removable = true;
		}
		//@ts-ignore
		if (
			properties?.schema?.properties?.options?.properties?.readOnly &&
			typeof originalData?.options?.readOnly === "undefined"
		) {
			optionDefaults.readOnly = false;
		}

		// Apply defaults if any exist
		if (Object.keys(optionDefaults).length > 0) {
			originalData = {
				...originalData,
				options: {
					...originalData.options,
					...optionDefaults,
				},
			};
		}

		return originalData;
	}, [uiElement, properties]);

	useEffect(() => {
		setLocalData(data);
	}, [data]);

	const debouncedUpdate = useMemo(
		() =>
			debounce((updatedProperties: any, elementUuid: string) => {
				dispatch(Actions.updateUISchemaElement(elementUuid, updatedProperties));
			}, 300),
		[dispatch]
	);

	const updateProperties = useCallback(
		({ data: updatedProperties }: { data: Partial<CustomUISchemaElement> }) => {
			const isEmptyString = (v: unknown) => typeof v === "string" && v.trim() === "";
			if (updatedProperties) {
				if (isEmptyString(updatedProperties.label)) {
					updatedProperties.label = undefined;
				}
				if (isEmptyString(updatedProperties?.subText)) {
					updatedProperties.subText = undefined;
				}
			}

			// Immediate local update for responsive UI
			setLocalData(updatedProperties);

			if (uiElement?.uuid && !isEqual(data, updatedProperties)) {
				debouncedUpdate(updatedProperties, uiElement.uuid);
			}
		},
		[uiElement?.uuid, data, debouncedUpdate]
	);

	useEffect(() => {
		return () => {
			debouncedUpdate.cancel();
		};
	}, [debouncedUpdate]);

	const memoizedProperties = useMemo(() => {
		if (!uiElement) return undefined;

		let linkedSchemaUUID = uiElement.linkedSchemaElement;
		if (!linkedSchemaUUID) {
			let parent = uiElement?.parent;
			while (!linkedSchemaUUID && parent) {
				if (parent.linkedSchemaElement) {
					linkedSchemaUUID = parent.linkedSchemaElement;
				}
				parent = parent.parent;
			}
		}

		const elementSchema = linkedSchemaUUID && schema ? tryFindByUUID(schema, linkedSchemaUUID) : undefined;
		let properties = propertiesService.getProperties(uiElement, elementSchema);

		if (!properties) return undefined;

		// Clone to avoid mutations
		properties = {
			schema: JSON.parse(JSON.stringify(properties.schema)),
			uiSchema: JSON.parse(JSON.stringify(properties.uiSchema)),
		};

		// Add additional properties based on element type
		if (elementSchema?.schema.type && uiElement.type === "Control") {
			addSchemaOptionsProperty(properties.schema, {
				readOnly: { type: "boolean", default: false },
			});
			(properties.uiSchema as Layout).elements.push(createPropertyControl("#/properties/options/properties/readOnly"));
		}

		if (elementSchema?.schema.type === "array" && !elementSchema?.schema.format && uiElement.type === "Control") {
			addSchemaOptionsProperty(properties.schema, {
				removable: { type: "boolean", default: true },
				addable: { type: "boolean", default: true },
			});
			(properties.uiSchema as Layout).elements.push(
				createPropertyControl("#/properties/options/properties/removable"),
				createPropertyControl("#/properties/options/properties/addable")
			);
		}

		if (elementSchema?.schema.type === "string" && !elementSchema?.schema.format && uiElement.type === "Control") {
			addSchemaOptionsProperty(properties.schema, {
				multi: { type: "boolean" },
			});
			(properties.uiSchema as Layout).elements.push(createPropertyControl("#/properties/options/properties/multi"));
		}

		return properties;
	}, [uiElement, schema, propertiesService]);

	useEffect(() => {
		setProperties(memoizedProperties);
	}, [memoizedProperties]);

	if (!selection) return <NoSelection />;

	return properties ? (
		<JsonForms
			data={localData}
			schema={properties.schema}
			uischema={properties.uiSchema}
			onChange={updateProperties}
			renderers={propertyRenderers}
			cells={materialCells}
		/>
	) : (
		<NoProperties />
	);
};

const NoSelection = () => <div>No selection</div>;
const NoProperties = () => <div>Selected element does not have any configurable properties.</div>;
